import type { IconProps } from "./icons";
import {
  IconCheck,
  IconCross,
  IconHalf,
  IconMute,
  IconRing,
  IconSquare,
  IconTriangle,
} from "./icons";

/**
 * Colour is never the only signal: every tone below always renders with an icon
 * (a distinct *shape*, not just a hue) and a text label. This mapping is the one
 * place that decision lives, so every status surface in the app stays consistent
 * and colour-blind safe.
 */
export type Tone = "critical" | "warning" | "caution" | "info" | "positive" | "neutral" | "suppressed";

export interface StatusMeta {
  label: string;
  tone: Tone;
  Icon: (props: IconProps) => React.ReactElement;
  /** Short 2-5 char code for dense table cells where a full label doesn't fit. */
  code: string;
}

export const TONE_CLASSES: Record<Tone, string> = {
  critical: "text-[var(--tone-critical)] bg-[var(--tone-critical-bg)] border-[var(--tone-critical-border)]",
  warning: "text-[var(--tone-warning)] bg-[var(--tone-warning-bg)] border-[var(--tone-warning-border)]",
  caution: "text-[var(--tone-caution)] bg-[var(--tone-caution-bg)] border-[var(--tone-caution-border)]",
  info: "text-[var(--tone-info)] bg-[var(--tone-info-bg)] border-[var(--tone-info-border)]",
  positive: "text-[var(--tone-positive)] bg-[var(--tone-positive-bg)] border-[var(--tone-positive-border)]",
  neutral: "text-[var(--tone-neutral)] bg-[var(--tone-neutral-bg)] border-[var(--tone-neutral-border)]",
  suppressed: "text-[var(--tone-suppressed)] bg-[var(--tone-suppressed-bg)] border-[var(--tone-suppressed-border)]",
};

export type StatusDomain =
  | "asset"
  | "check"
  | "alert"
  | "incident"
  | "agent"
  | "verification"
  | "report"
  | "scope-verification";

const ASSET_STATUS: Record<string, StatusMeta> = {
  pending: { label: "Pending", tone: "neutral", Icon: IconRing, code: "PEND" },
  healthy: { label: "Healthy", tone: "positive", Icon: IconCheck, code: "OK" },
  degraded: { label: "Degraded", tone: "warning", Icon: IconHalf, code: "DEG" },
  down: { label: "Down", tone: "critical", Icon: IconCross, code: "DOWN" },
  maintenance: { label: "Maintenance", tone: "info", Icon: IconSquare, code: "MAINT" },
  unknown: { label: "Unknown", tone: "neutral", Icon: IconRing, code: "UNK" },
  retired: { label: "Retired", tone: "neutral", Icon: IconSquare, code: "RET" },
  deleted: { label: "Deleted", tone: "neutral", Icon: IconCross, code: "DEL" },
};

const CHECK_STATUS: Record<string, StatusMeta> = {
  pending: { label: "Pending", tone: "neutral", Icon: IconRing, code: "PEND" },
  passing: { label: "Passing", tone: "positive", Icon: IconCheck, code: "PASS" },
  failing: { label: "Failing", tone: "critical", Icon: IconCross, code: "FAIL" },
  degraded: { label: "Degraded", tone: "warning", Icon: IconHalf, code: "DEG" },
  unknown: { label: "Unknown", tone: "neutral", Icon: IconRing, code: "UNK" },
  disabled: { label: "Disabled", tone: "neutral", Icon: IconSquare, code: "OFF" },
};

const ALERT_STATE: Record<string, StatusMeta> = {
  pending: { label: "Pending", tone: "neutral", Icon: IconRing, code: "PEND" },
  firing: { label: "Firing", tone: "critical", Icon: IconTriangle, code: "FIRE" },
  resolved: { label: "Resolved", tone: "positive", Icon: IconCheck, code: "RES" },
  suppressed: { label: "Suppressed", tone: "suppressed", Icon: IconMute, code: "SUPP" },
};

const INCIDENT_STATUS: Record<string, StatusMeta> = {
  open: { label: "Open", tone: "critical", Icon: IconTriangle, code: "OPEN" },
  acknowledged: { label: "Acknowledged", tone: "warning", Icon: IconHalf, code: "ACK" },
  investigating: { label: "Investigating", tone: "warning", Icon: IconHalf, code: "INV" },
  mitigating: { label: "Mitigating", tone: "caution", Icon: IconHalf, code: "MIT" },
  monitoring: { label: "Monitoring", tone: "info", Icon: IconRing, code: "MON" },
  resolved: { label: "Resolved", tone: "positive", Icon: IconCheck, code: "RES" },
  closed: { label: "Closed", tone: "neutral", Icon: IconSquare, code: "CLOSE" },
  cancelled: { label: "Cancelled", tone: "neutral", Icon: IconCross, code: "CANC" },
};

const AGENT_STATUS: Record<string, StatusMeta> = {
  pending: { label: "Pending", tone: "neutral", Icon: IconRing, code: "PEND" },
  active: { label: "Active", tone: "positive", Icon: IconCheck, code: "UP" },
  degraded: { label: "Degraded", tone: "warning", Icon: IconHalf, code: "DEG" },
  offline: { label: "Offline", tone: "critical", Icon: IconCross, code: "OFF" },
  revoked: { label: "Revoked", tone: "neutral", Icon: IconSquare, code: "REV" },
};

const VERIFICATION_STATUS: Record<string, StatusMeta> = {
  pending: { label: "Pending", tone: "neutral", Icon: IconRing, code: "PEND" },
  verified: { label: "Verified", tone: "positive", Icon: IconCheck, code: "OK" },
  failed: { label: "Failed", tone: "critical", Icon: IconCross, code: "FAIL" },
  expired: { label: "Expired", tone: "warning", Icon: IconSquare, code: "EXP" },
  revoked: { label: "Revoked", tone: "neutral", Icon: IconSquare, code: "REV" },
};

const REPORT_STATUS: Record<string, StatusMeta> = {
  queued: { label: "Queued", tone: "neutral", Icon: IconRing, code: "QUE" },
  running: { label: "Running", tone: "info", Icon: IconHalf, code: "RUN" },
  completed: { label: "Completed", tone: "positive", Icon: IconCheck, code: "DONE" },
  failed: { label: "Failed", tone: "critical", Icon: IconCross, code: "FAIL" },
};

const REGISTRY: Record<StatusDomain, Record<string, StatusMeta>> = {
  asset: ASSET_STATUS,
  check: CHECK_STATUS,
  alert: ALERT_STATE,
  incident: INCIDENT_STATUS,
  agent: AGENT_STATUS,
  verification: VERIFICATION_STATUS,
  "scope-verification": VERIFICATION_STATUS,
  report: REPORT_STATUS,
};

const FALLBACK: StatusMeta = { label: "Unknown", tone: "neutral", Icon: IconRing, code: "?" };

export function getStatusMeta(domain: StatusDomain, value: string): StatusMeta {
  return REGISTRY[domain]?.[value] ?? { ...FALLBACK, label: value };
}
